import React, { useState, useCallback, useRef } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DiscoveryActions from './DiscoveryActions';
import useDiscoveryRowActions from '../hooks/useDiscoveryRowActions';

/**
 * A generic hover wrapper for any stock row in Stock Discovery.
 * On desktop: shows Buy/Sell pill on mouse hover.
 * On mobile: renders children only, no hover logic at all.
 *
 * @param {object} stock - The raw stock object from the Discovery API.
 * @param {ReactNode} children - The row's existing column UI.
 */
const HoverableStockRow = ({ stock, children }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isHovered, setIsHovered] = useState(false);
  const leaveTimerRef = useRef(null);
  const { handleBuy, handleSell, userSettings } = useDiscoveryRowActions();

  // Map raw API stock object to the instrument shape expected by the order window.
  // ... (keeping existing instrument mapping) ...
  const resolvedToken = stock.token || stock.instrument_token || stock.exchange_token || 0;
  const hasValidToken = !!(resolvedToken && Number(resolvedToken) > 0);
  const exchangeStr = (stock.exchange || 'NSE').toUpperCase();
  const isIndex = exchangeStr.includes('INDICES');

  const instrument = {
    token: Number(resolvedToken),
    exchange: exchangeStr,
    trading_symbol: stock.trading_symbol || stock.symbol || '',
    display_name: stock.display_name || stock.trading_symbol || stock.symbol || '',
    company_name: stock.company_name || '',
    instrument_name: stock.instrument_name || '',
    lot_size: stock.lot_size || 1,
    series: stock.series || '',
    tradable: hasValidToken && !isIndex,
    price_precision: stock.price_precision ?? 2,
    ltp: stock.ltp || 0,
  };

  const handleRowClick = useCallback((e) => {
    // If it's a mobile device or if the user clicked the row (not the buttons)
    if (hasValidToken) {
      const precision = instrument.price_precision || 0;
      navigate(`/overview/tdv/${instrument.exchange}/${instrument.token}/${precision}`, {
        state: { defaultTab: 'chart' }
      });
    }
  }, [hasValidToken, instrument, navigate]);

  const handleMouseEnter = useCallback(() => {
    if (isMobile) return;
    if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    setIsHovered(true);
  }, [isMobile]);

  const handleMouseLeave = useCallback(() => {
    leaveTimerRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 0);
  }, []);

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleRowClick}
      sx={{ 
        position: 'relative', 
        overflow: 'visible',
        cursor: hasValidToken ? 'pointer' : 'default',
        '&:hover': {
          bgcolor: 'action.hover'
        }
      }}
    >
      {/* Row's existing column layout */}
      {children}

      {/* Hover overlay — desktop only. */}
      {!isMobile && isHovered && (
        <DiscoveryActions
          instrument={instrument}
          onBuy={handleBuy}
          onSell={handleSell}
          userSettings={userSettings}
        />
      )}
    </Box>
  );
};

export default HoverableStockRow;

